import {Component, Inject, OnInit} from '@angular/core';

import {moveItemInArray} from '@angular/cdk/drag-drop';

import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {Subject} from 'rxjs';

import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';

import {Entry, Group, ParticipantMember, Timezone} from 'src/app/models';
import {GroupsService, IndividualsService, SchedulesService, SnackbarService} from 'src/app/services';
import {ScheduleCreateComponent} from '../schedule-create/schedule-create.component';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';

dayjs.extend(utc);
dayjs.extend(timezone);

@Component({
    selector: 'app-schedule-update',
    templateUrl: './schedule-update.component.html',
    styleUrls: ['./schedule-update.component.scss', '../schedule-shared.scss']
})
export class ScheduleUpdateComponent implements OnInit {
    currentGroup: Group;
    scheduleList: Entry[];
    updatedScheduleList: Entry[];
    entryIdsToRemove: number[] = [];
    individualChange = new Subject<string>();
    groupChange = new Subject<string>();
    individualInputs = [];
    individualsAndDevices: any;
    autocompleteIndOrDevice = [];
    startDate = [];
    endDate = [];
    scheduleTypes = ['weekly', 'daily'];
    participant_type = '';
    closeDialog = false;
    ready = false;
    hourglass = false;
    timezone: Timezone;
    defaultID;
    days = [
        {'id': 2, 'name': 'Monday', 'short': 'Mon'},
        {'id': 3, 'name': 'Tuesday', 'short': 'Tue'},
        {'id': 4, 'name': 'Wednesday', 'short': 'Wed'},
        {'id': 5, 'name': 'Thursday', 'short': 'Thu'},
        {'id': 6, 'name': 'Friday', 'short': 'Fri'},
        {'id': 7, 'name': 'Saturday', 'short': 'Sat'},
        {'id': 1, 'name': 'Sunday', 'short': 'Sun'}
    ];

    constructor(@Inject(MAT_DIALOG_DATA) public data: any,
                private dialog: MatDialog,
                private scheduleService: SchedulesService,
                private individualsService: IndividualsService,
                private groupsService: GroupsService,
                private snackbarService: SnackbarService,
                public dialogRef: MatDialogRef<ScheduleUpdateComponent>) {
    }

    /**
     * Reset ordinals after reordering and/or removing entries or participants.
     */
    private static updateOrdinals(arr) {
        for (let i = 0; i < arr.length; i++) {
            arr[i].ordinal = i;
        }
    }

    ngOnInit() {
        const delay = 750;
        this.individualChange.pipe(
            debounceTime(delay),
            distinctUntilChanged()
        ).subscribe((term: string) => this.getIndividualsAndDevices(term.trim()));
        this.groupChange.pipe(
            debounceTime(delay),
            distinctUntilChanged()
        ).subscribe((term: string) => this.getGroupNames(term.trim()));

        this.currentGroup = this.data.dataKey;
        this.defaultID = this.data.schedule[0].timezone;
        this.scheduleList = this.data.schedule;
        this.updatedScheduleList = this.scheduleList;
        this.individualInputs = Array.from({length: this.scheduleList.length});
        this.scheduleList.forEach((event) => {
            this.startDate.push(dayjs(event.starts).tz(this.defaultID).format('YYYY-MM-DDTHH:mm'));
            this.endDate.push(!event.ends ? null : dayjs(event.ends).tz(this.defaultID).format('YYYY-MM-DDTHH:mm'));
        });
        this.ready = true;
    }

    handleTimezoneChange(myTimezone: Timezone) {
        if (this.timezone !== myTimezone) {
            this.timezone = myTimezone;
            this.scheduleList.forEach((event) => {
                this.startDate.push(dayjs(event.starts).tz(this.timezone.id).format('YYYY-MM-DDTHH:mm'));
                this.endDate.push(!event.ends ? null : dayjs(event.ends).tz(this.timezone.id).format('YYYY-MM-DDTHH:mm'));
            });
        }
    }

    getIndividualsAndDevices(filterValueName: string) {
        if (filterValueName != null && filterValueName.length > 1) {
            this.individualsService.getIndividualsAndDevices(filterValueName).subscribe(individAndDevices => {
                this.individualsAndDevices = individAndDevices.content;
                this.autocompleteIndOrDevice = [];
                this.individualsAndDevices.forEach(e => {
                    this.autocompleteIndOrDevice.push(e);
                });
            });
        } else {
            this.individualsAndDevices = [];
        }
    }

    getGroupNames(filterValueGroup: string) {
        if (filterValueGroup != null && filterValueGroup.length > 1) {
            this.groupsService.getGroupNames(filterValueGroup).subscribe(groups => {
                this.individualsAndDevices = groups;
                this.autocompleteIndOrDevice = [];
                this.individualsAndDevices.forEach(e => {
                    this.autocompleteIndOrDevice.push(e);
                });
            });
        } else {
            this.individualsAndDevices = [];
        }
    }

    indChange($event) {
        this.individualChange.next($event);
    }

    grpChange($event) {
        this.groupChange.next($event);
    }

    /**
     * When focus is on Duration input, stop event propagation to the parent mat-chip.
     */
    onChangeDuration(event: KeyboardEvent) {
        event.stopPropagation();
        this.clearNextRotation();
    }

    removeParticipant(participant) {
        this.updatedScheduleList.forEach(entry => {
            if (entry.entry_id === participant.entry_id) {
                entry.participants = entry.participants.filter(p => p !== participant);
            }
        });
        this.clearNextRotation();
    }

    setupParticipant(event: any, entry: Entry, index: any): any {
        const newParticipant: any = new ParticipantMember();
        newParticipant.active = false;
        newParticipant.duration = entry.frequency_name === 'daily' ? 24 : 168;
        newParticipant.entry_id = entry.entry_id;
        newParticipant.ordinal = entry.participants.length === 0 ? 0 : entry.participants[entry.participants.length - 1].ordinal + 1;
        newParticipant.participant_id = event.id;
        newParticipant.participant_name = (event.full_name ? event.full_name : event.name);
        newParticipant.participant_type = (event.full_name ? 'INDIVIDUAL' : (event.group_type ? 'GROUP' : 'DEVICE'));
        if (this.currentGroup.id === event.id && !!event.group_type) {
            this.snackbarService.openSnackBar('Cannot add ' + newParticipant.participant_name + ' to it\'s own schedule', true);
        } else {
            entry.participants.push(newParticipant);
        }
        this.individualInputs[index] = '';
        this.clearNextRotation();
    }

    clear(index) {
        this.individualInputs[index] = '';
        this.autocompleteIndOrDevice = [];
    }

    save() {
        this.hourglass = true;
        if (this.testValidity()) {
            ScheduleUpdateComponent.updateOrdinals(this.scheduleList);
            this.scheduleList.forEach((entry, i) => {
                entry.starts = dayjs.tz(this.startDate[i], this.timezone.id).utc().valueOf();
                entry.ends = !this.endDate || !this.endDate[i] ? null : dayjs.tz(this.endDate[i], this.timezone.id).utc().valueOf();
                ScheduleUpdateComponent.updateOrdinals(entry.participants);
                entry.timezone = this.timezone.id;
            });
            this.scheduleService.updateSchedule(this.scheduleList).subscribe({
                next: () => {
                    let failed = false;
                    this.entryIdsToRemove.forEach(id => {
                        this.scheduleService.deleteScheduleEntry(id).subscribe({
                            next: () => {
                            }, error: err => {
                                failed = true;
                                this.snackbarService.openSnackBar(err.statusText.toString() + ' : Could not delete entry.', true);
                            }
                        });
                    });
                    if (!failed) {
                        this.snackbarService.openSnackBar('Schedule Updated Successfully', false);
                    }
                    this.hourglass = false;
                    this.dialogRef.close(true);
                }, error: () => {
                    this.hourglass = false;
                    this.snackbarService.openSnackBar('No updates made.', true);
                }
            });
        } else {
            this.hourglass = false;
        }
    }

    testValidity(): boolean {
        for (let i = 0; i < this.updatedScheduleList.length; i++) {
            const entry = this.updatedScheduleList[i];
            if (entry.entry_name === '' || entry.entry_name === undefined) {
                this.snackbarService.openSnackBar('Please define the name for all entries.', true);
                return false;
            } else if (this.startDate[i] === '' || !this.startDate[i] || this.startDate[i] == "Invalid Date") {
                this.snackbarService.openSnackBar('Please define a valid start time and date for entry ' + entry.entry_name + ' and try again.', true);
                return false;
            } else if (this.verifyDates(this.startDate[i], this.endDate[i])) {
                this.snackbarService.openSnackBar('Please make sure the start date is before the end date and try again.', true);
                return false;
            } else if ((entry.frequency_name === 'weekly') && (entry.days_of_week.length === 0)) {
                this.snackbarService.openSnackBar('Please define the days of the week for entry ' + entry.entry_name + ' and try again.', true);
                return false;
            } else if (!(entry.participants.length > 0)) {
                this.snackbarService.openSnackBar('Please define the participants assigned to entry ' + entry.entry_name + ' and try again.', true);
                return false;
            } else {
                for (const participant of entry.participants) {
                    if (participant.duration === undefined || participant.duration % .25 !== 0 || participant.duration < .25) {
                        this.snackbarService.openSnackBar(
                            'Please enter a positive duration in increments of .25 for ' + participant.participant_name + ' in ' + entry.entry_name + '.', true);
                        return false;
                    }
                }
            }
        }
        return true;
    }

    changeDay(entry: Entry, day) {
        if (entry.days_of_week.some(entryDay => entryDay.id === day.id)) {
            entry.days_of_week = entry.days_of_week.filter(entryDay => entryDay.id !== day.id);
        } else {
            entry.days_of_week.push({id: day.id, name: day.name});
        }
    }

    containsDay(entry: Entry, day) {
        return entry.days_of_week.some(entryDay => entryDay.name.toLowerCase() === day.toLowerCase());
    }

    removeEntry(entryID) {
        this.entryIdsToRemove.push(entryID);
        this.updatedScheduleList = this.updatedScheduleList.filter(entry => entry.entry_id !== entryID);
        ScheduleUpdateComponent.updateOrdinals(this.updatedScheduleList);
    }

    drop(event: any, entry: any) {
        moveItemInArray(entry.participants, event.previousIndex, event.currentIndex);
        ScheduleUpdateComponent.updateOrdinals(entry.participants);
        this.clearNextRotation();
    }

    schedulePopup() {
        const popupDialog = this.dialog.open(ScheduleCreateComponent, {
            disableClose: true,
            width: '80%',
            data: {
                dataKey: this.currentGroup,
                entriesList: this.updatedScheduleList,
                entryIdsToRemove: this.entryIdsToRemove,
                currentTimezone: this.timezone,
                disableTimezone: this.updatedScheduleList.length > 0
            }
        });

        popupDialog.afterClosed().subscribe(statement => {
            if (statement) {
                this.handleNewEntry();
            }
        });
    }

    handleNewEntry() {
        this.closeDialog = true;
        this.ready = false;

        this.startDate = [];
        this.endDate = [];
        this.scheduleService.getScheduleByGroupID(this.currentGroup.id).subscribe(sched => {
            this.scheduleList = sched;
            this.scheduleList.forEach((entry) => {
                this.startDate.push(dayjs.tz(entry.starts, entry.timezone).format('YYYY-MM-DDTHH:mm'));
                this.endDate.push(!entry.ends ? null : dayjs.tz(entry.ends, entry.timezone).format('YYYY-MM-DDTHH:mm'));
            });
            this.updatedScheduleList = this.scheduleList;
            this.individualInputs = Array.from({length: this.scheduleList.length});

            this.ready = true;
        });
    }

    moveEntryUp(entry: any) {
        moveItemInArray(this.updatedScheduleList, entry.ordinal, entry.ordinal - 1);
        moveItemInArray(this.startDate, entry.ordinal, entry.ordinal - 1);
        moveItemInArray(this.endDate, entry.ordinal, entry.ordinal - 1);
        ScheduleUpdateComponent.updateOrdinals(this.updatedScheduleList);
    }

    moveEntryDown(entry: any) {
        moveItemInArray(this.updatedScheduleList, entry.ordinal, entry.ordinal + 1);
        moveItemInArray(this.startDate, entry.ordinal, entry.ordinal + 1);
        moveItemInArray(this.endDate, entry.ordinal, entry.ordinal + 1);
        ScheduleUpdateComponent.updateOrdinals(this.updatedScheduleList);
    }

    isGroup(entry: any) {
        return entry.isGroup ? entry.isGroup : entry.participants[0] ? entry.participants[0].participant_type === 'GROUP' : false;
    }

    clearNextRotation() {
        this.updatedScheduleList.forEach(entry => {
                entry.next = undefined;
            }
        );
    }

    makeEvenMultiple(num: number): number {
        const justDecimal = (Math.round((num % 1) * 100)) / 100;
        return justDecimal === .7 ? num - (justDecimal) + .75 : (justDecimal === .2 ? num - (justDecimal) + .25 : num - (num % .25));
    }

    getRotationTimezone(time) {
        return time ? dayjs.tz(time, this.defaultID).tz(this.defaultID).format('YYYY-MM-DDTHH:mm') : undefined;
    }

    // Test If Start Date Is Before End Date
    // Note: this does NOT test if there is a start date.  That is done in the testValidity() function.
    verifyDates(startDate, endDate) {
        if (startDate == null || endDate == null) {
            // Since There Is Not a Start or End Date, We Do Not Need To Test That THe Start Dates Comes First
            return false;
        }
        return new Date(startDate) > new Date(endDate);
    }
}
